import React from 'react';
import { Card } from '@/components/ui/Card';

export type ImageIntent = 'use_uploaded' | 'generate_variations' | 'generate_new';

export interface MediaIntentCardProps {
  filenames: string[];
  onIntent: (intent: ImageIntent) => void;
}

const OPTIONS: { intent: ImageIntent; label: string; description: string; icon: React.ReactNode }[] = [
  {
    intent: 'use_uploaded',
    label: 'Use my images',
    description: 'Add them to the campaign as-is, no AI generation',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    intent: 'generate_variations',
    label: 'Generate variations',
    description: 'Create AI variations inspired by my uploaded images',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    intent: 'generate_new',
    label: 'Generate new images',
    description: 'Let AI create brand-new images for this campaign',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
];

export const MediaIntentCard: React.FC<MediaIntentCardProps> = ({ filenames, onIntent }) => {
  const imageCount = filenames.length;

  return (
    <Card variant="outlined" padding="none" className="overflow-hidden">
      {/* Header — matches MediaProposalCard style */}
      <div className="p-5 bg-gradient-to-r from-purple-500/5 to-pink-500/5 border-b border-line dark:border-border-dark">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <div>
            <h3 className="text-base font-semibold text-ink">
              What would you like to do with {imageCount === 1 ? 'this image' : `these ${imageCount} images`}?
            </h3>
            <p className="text-xs text-ink-3 mt-0.5">
              {filenames.join(', ')}
            </p>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="p-4 flex flex-col sm:flex-row gap-3">
        {OPTIONS.map(({ intent, label, description, icon }) => (
          <button
            key={intent}
            onClick={() => onIntent(intent)}
            className="flex-1 text-left rounded-lg border-2 border-line dark:border-border-dark hover:border-purple-400 hover:bg-purple-50 p-4 transition-all duration-150 group"
          >
            <div className="flex items-center gap-2 text-ink-2 group-hover:text-purple-700 mb-1.5">
              {icon}
              <span className="font-medium text-sm">{label}</span>
            </div>
            <p className="text-xs text-ink-3 leading-relaxed">
              {description}
            </p>
          </button>
        ))}
      </div>
    </Card>
  );
};

export default MediaIntentCard;
